import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { WHATSAPP_URL } from "../App";

const t = {
  es: {
    home: "Inicio",
    about: "Nosotros",
    services: "Servicios",
    gallery: "Galería",
    prices: "Precios",
    faq: "FAQ",
    contact: "Contacto",
    book: "Reservar",
  },
  en: {
    home: "Home",
    about: "About",
    services: "Services",
    gallery: "Gallery",
    prices: "Prices",
    faq: "FAQ",
    contact: "Contact",
    book: "Book Now",
  },
};

const navLinks = [
  { key: "home", href: "#hero" },
  { key: "about", href: "#about" },
  { key: "services", href: "#services" },
  { key: "gallery", href: "#gallery" },
  { key: "prices", href: "#pricing" },
  { key: "faq", href: "#faq" },
  { key: "contact", href: "#contact" },
];

export default function Navbar({ lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const tr = t[lang];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-[#1F3A5E]/10"
          : "bg-[#1F3A5E]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          data-testid="navbar-logo"
          className="flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#D4B895]">
            <img
              src="/images/logo.jpg"
              alt="Berlisa del Pacífico"
              className="w-full h-full object-cover"
            />
          </div>
          <span className={`font-montserrat font-bold text-lg hidden sm:block transition-colors duration-300 ${scrolled ? 'text-[#1F3A5E]' : 'text-white'}`}>
            Berlisa del Pacífico
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              data-testid={`nav-${link.key}`}
              className={`font-montserrat text-sm font-medium transition-colors duration-200 relative group ${
            scrolled ? "text-[#1F3A5E]/70 hover:text-[#1F3A5E]" : "text-white/80 hover:text-white"
          }`}
            >
              {tr[link.key]}
              <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-[#8C5E35] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Language Toggle */}
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className={`hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-montserrat font-semibold transition-colors duration-200 ${
              scrolled
                ? "border-[#1F3A5E]/20 text-[#1F3A5E] hover:bg-[#1F3A5E]/5"
                : "border-white/40 text-white hover:bg-white/10"
            }`}
          >
            <span className={lang === "es" ? "opacity-100" : "opacity-40"}>ES</span>
            <span className="opacity-30">/</span>
            <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
          </button>

          {/* Book Now */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-btn"
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-montserrat font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200 ${
              scrolled
                ? "bg-[#1F3A5E] text-white hover:shadow-[#1F3A5E]/20"
                : "bg-white text-[#1F3A5E] hover:shadow-white/20"
            }`}
          >
            {tr.book}
          </a>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 transition-colors duration-200 ${scrolled ? "text-[#1F3A5E]" : "text-white"}`}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#1F3A5E]/10 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="font-montserrat text-sm font-medium text-[#1F3A5E] py-2 border-b border-[#1F3A5E]/5"
            >
              {tr[link.key]}
            </a>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="px-3 py-1.5 rounded-full border border-[#1F3A5E]/20 text-xs font-montserrat font-semibold text-[#1F3A5E]"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center py-2.5 bg-[#1F3A5E] text-white rounded-full text-sm font-montserrat font-semibold"
            >
              {tr.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
